/**
 * The effective working-time assumptions for ONE role, and where each one came
 * from.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHY ORIGIN TRAVELS WITH THE VALUE, IN ONE OBJECT
 * ══════════════════════════════════════════════════════════════════════════
 *
 * A coverage percentage is a customer-facing number derived from three
 * assumptions the customer chose — the working day, the working week, the
 * working year. `coverage-time-unit.types.ts` states the stakes: 46 working
 * weeks against 52 moves a coverage figure by 13%. So a reader who cannot see
 * which assumptions produced the figure cannot check it, and a reader shown
 * assumptions that are not the ones the engine used is worse off than one shown
 * none at all.
 *
 * The organisation states a default; a role may override any subset of it. That
 * is two sources for three numbers, and the failure mode is not exotic: report
 * the organisation's 52 while dividing by the role's 46 and every figure on the
 * page is unreproducible, with nothing anywhere reading as wrong.
 *
 * This module makes that specific disagreement UNREPRESENTABLE rather than
 * merely tested:
 *
 * 1. {@link resolveCoverageWorkingTime} is the ONLY place a value is chosen
 *    between the two sources, and it emits the value and its origin as one
 *    pair, in one pass.
 * 2. {@link ResolvedCoverageWorkingTime} is BRANDED, so nothing else can mint
 *    one — a caller cannot hand-assemble a plausible `{value, origin}` set.
 * 3. {@link toCoverageWorkingTimeSettings} — the only thing the evaluator is
 *    given — reads `.value` off that same object, and
 *    {@link coverageContextForRole} is the only sanctioned way to reach the
 *    evaluator from a role.
 *
 * So the number the UI displays and the number the arithmetic used are the same
 * field of the same object. To make them disagree you have to edit this file,
 * and the tests here fail when you do.
 *
 * ── `null` MEANS "NO OVERRIDE", AND `!== undefined` ALONE IS THE BUG ─────────
 *
 * The override arrives from three NULLABLE columns, so "this role does not
 * override the working week" is spelled `null`, not absent. A guard written
 * `override.daysPerWeek !== undefined` therefore accepts `null` as an override
 * and feeds it forward as a value — where it is refused, if you are lucky, by
 * the context parser, and silently divides by zero if you are not. Every pick
 * in this file goes through {@link pick}, which tests both, and a test pins the
 * `null` case specifically.
 *
 * ── WHAT THIS DELIBERATELY DOES NOT DO ──────────────────────────────────────
 *
 * It does not validate. A zero or negative assumption is refused once, at
 * {@link coverageContextForRole}, by the schema that already refuses it for
 * every other caller — `coverage-context.schema.ts`. Resolving and validating
 * in one function would give this module a second opinion about what a legal
 * working day is, and two opinions drift.
 */
import {
  type CoverageContextParseFailure,
  parseCoverageContext,
  type ValidatedCoverageContext
} from "./coverage-context.schema";
import type { CoverageResult } from "./coverage-result.types";
import type { CoverageWorkingTimeSettings } from "./coverage-time-unit.types";

/** Where one effective working-time assumption came from. */
export const CoverageWorkingTimeOrigin = {
  /** The organisation's default, because this role overrides nothing here. */
  ORGANIZATION: "ORGANIZATION",
  /** This role's own override, which replaces the organisation's default. */
  ROLE: "ROLE"
} as const;

export type CoverageWorkingTimeOrigin =
  (typeof CoverageWorkingTimeOrigin)[keyof typeof CoverageWorkingTimeOrigin];

export const COVERAGE_WORKING_TIME_ORIGINS = Object.values(CoverageWorkingTimeOrigin) as [
  CoverageWorkingTimeOrigin,
  ...CoverageWorkingTimeOrigin[]
];

/** One effective assumption: the number in force, and who chose it. */
export interface ResolvedCoverageWorkingTimeSetting {
  readonly value: number;
  readonly origin: CoverageWorkingTimeOrigin;
}

/**
 * Proof that a working-time resolution came from {@link resolveCoverageWorkingTime}.
 *
 * Same idiom, and same purpose, as the validation brands in
 * `coverage-formula.schema.ts` and `coverage-context.schema.ts`: it makes "did
 * anything reconcile these values against their origins?" a question the
 * compiler answers, rather than one you answer by reading call sites. Phantom —
 * it costs nothing at runtime and never reaches stored JSON or an API response.
 */
export interface CoverageWorkingTimeResolutionBrand {
  readonly __coverageWorkingTimeResolved: true;
}

/** The three effective assumptions, each carrying its origin. */
export interface CoverageWorkingTimeResolution {
  readonly hoursPerDay: ResolvedCoverageWorkingTimeSetting;
  readonly daysPerWeek: ResolvedCoverageWorkingTimeSetting;
  readonly workingWeeksPerYear: ResolvedCoverageWorkingTimeSetting;
}

/** A resolution that provably came from {@link resolveCoverageWorkingTime}. */
export type ResolvedCoverageWorkingTime = CoverageWorkingTimeResolution &
  CoverageWorkingTimeResolutionBrand;

/**
 * A role's override of the organisation's assumptions.
 *
 * Every field is independently optional because the override is per FIELD: a
 * role that works a four-day week on the organisation's standard year overrides
 * `daysPerWeek` and nothing else, and the read contract has to be able to say
 * so. `null` and absent both mean "no override" — see the file header.
 */
export interface CoverageWorkingTimeOverride {
  readonly hoursPerDay?: number | null;
  readonly daysPerWeek?: number | null;
  readonly workingWeeksPerYear?: number | null;
}

/**
 * Choose between the organisation's default and this role's override, and say
 * which was chosen.
 *
 * The single decision point. Returning the value and the origin together is the
 * whole mechanism: there is no arrangement of these two sources that yields a
 * value from one and an origin from the other.
 */
export function resolveCoverageWorkingTime(
  organizationDefaults: CoverageWorkingTimeSettings,
  roleOverride: CoverageWorkingTimeOverride | null | undefined
): ResolvedCoverageWorkingTime {
  const override = roleOverride ?? {};
  const resolution: CoverageWorkingTimeResolution = {
    hoursPerDay: pick(organizationDefaults.hoursPerDay, override.hoursPerDay),
    daysPerWeek: pick(organizationDefaults.daysPerWeek, override.daysPerWeek),
    workingWeeksPerYear: pick(
      organizationDefaults.workingWeeksPerYear,
      override.workingWeeksPerYear
    )
  };
  // The single brand-minting cast for a resolution, confined here exactly as
  // the formula and context brands are confined to their own modules. The two
  // lines above ARE the definition of resolved.
  return resolution as ResolvedCoverageWorkingTime;
}

/**
 * The settings the evaluator is given, read off a resolution.
 *
 * Deliberately the ONLY projection: it takes `.value` from each pair the read
 * contract reports, so the arithmetic and the display cannot disagree. Widening
 * this to accept a bare {@link CoverageWorkingTimeSettings} would re-open the
 * hole this module exists to close.
 */
export function toCoverageWorkingTimeSettings(
  resolved: ResolvedCoverageWorkingTime
): CoverageWorkingTimeSettings {
  return {
    hoursPerDay: resolved.hoursPerDay.value,
    daysPerWeek: resolved.daysPerWeek.value,
    workingWeeksPerYear: resolved.workingWeeksPerYear.value
  };
}

/**
 * Build the evaluation context for one role.
 *
 * The one door from a resolution to the evaluator. It validates through
 * `parseCoverageContext`, so a zero or negative assumption — from either source
 * — is refused here and never reaches a division.
 *
 * `measuredInputs` is the channel that replaces an authored estimate with a
 * measured one: deriving a role's headcount from its actual members means
 * populating `{ headcount: <n> }` here, and nothing else changes — no column,
 * no schema, no contract. `RoleCoverage.basis` is already derived from what
 * that substitution produces.
 *
 * 🚨 **THIS PARAMETER IS THE ONLY THING BETWEEN A NUMBER AND THE WORD
 * `MEASURED` ON A SCREEN SHOWING MONEY.** No caller passes it today — which is
 * why every coverage figure the product serves reads ESTIMATED, correctly, and
 * why `MEASURED` is currently unreachable. Whoever wires the first producer
 * owns the provenance rule: `coverage-basis.ts` cannot tell an observed count
 * from one somebody typed, and it publishes the claim either way. A default, a
 * fallback or a convenience value here is a lie with a customer on the other
 * end of it.
 */
export function coverageContextForRole(
  resolved: ResolvedCoverageWorkingTime,
  measuredInputs?: Readonly<Record<string, number>>
): CoverageResult<ValidatedCoverageContext, CoverageContextParseFailure> {
  return parseCoverageContext({
    workingTime: toCoverageWorkingTimeSettings(resolved),
    ...(measuredInputs === undefined ? {} : { measuredInputs })
  });
}

/**
 * One field's decision.
 *
 * `null` is "no override" and so is `undefined`, because the override arrives
 * from nullable columns and a partial authoring API sends `null` to mean "leave
 * it alone". Testing only for `undefined` would take `null` as an override and
 * carry it forward as the effective value.
 */
function pick(
  organizationDefault: number,
  override: number | null | undefined
): ResolvedCoverageWorkingTimeSetting {
  if (override === undefined || override === null) {
    return { value: organizationDefault, origin: CoverageWorkingTimeOrigin.ORGANIZATION };
  }
  return { value: override, origin: CoverageWorkingTimeOrigin.ROLE };
}
